import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { ProjectTeam } from './team/ProjectTeam';
import { ProjectMaterials } from './materials/ProjectMaterials';
import { ProjectOverview } from './overview/ProjectOverview';
import { ProjectSetup } from './setup/ProjectSetup';
import { ProjectSpecifications } from './specifications/ProjectSpecifications';
import type { DashboardProject } from '../../types/project';
import { Pencil, ChevronRight, LayoutDashboard, Settings, FileText, Package, Users, BarChart2, LineChart, Hammer } from 'lucide-react';
import { ProjectProduction } from './production/ProjectProduction';
import { AssemblyProduction } from './production/AssemblyProduction';

interface ProjectDashboardProps {
  project: DashboardProject;
  onBack: () => void;
}

const DashedBoard: React.FC<ProjectDashboardProps> = ({ project, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedAssembly, setSelectedAssembly] = useState<string | null>(null);

  const TABS_CONFIG = {
    overview: {
      label: 'Overview',
      icon: LayoutDashboard,
      content: () => <div><ProjectOverview project={project} /></div>
    },
    setup: {
      label: 'Setup',
      icon: Settings,
      content: () => <div><ProjectSetup project={project} /></div>
    },
    specifications: {
      label: 'Specifications',
      icon: FileText,
      content: () => <div><ProjectSpecifications project={project} /></div>
    },
    'material-traceability': {
      label: 'Material Traceability',
      icon: Package,
      content: () => <div><ProjectMaterials project={project} /></div>
    },
    production: {
      label: 'Production',
      icon: Hammer,
      content: () => <div><ProjectProduction project={project} selectedAssembly={selectedAssembly} setSelectedAssembly={setSelectedAssembly} /></div>
    },
    'team-members': {
      label: 'Team Members',
      icon: Users,
      content: () => <div><ProjectTeam project={project} /></div>
    },
    reports: {
      label: 'Reports',
      icon: BarChart2,
      content: () => <div> <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Reports</h1>
        <p className="text-gray-600">Reports features coming soon...</p>
      </div></div>
    },
    analytics: {
      label: 'Analytics',
      icon: LineChart,
      content: () => <div> <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Analytics</h1>
        <p className="text-gray-600">Analytics features coming soon...</p>
      </div></div>
    }
  } as const;

  return (
    <div className="min-h-screen">
      <div className="max-w-[2000px] mx-auto px-2">
        <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-100px)]">
          {selectedAssembly ? (
            <AssemblyProduction
              project={project}
              onBack={onBack}
              selectedAssembly={selectedAssembly}
              setSelectedAssembly={setSelectedAssembly}
            />
          ) : (
              <div className="bg-white rounded-[0.5rem] shadow-sm min-h-[calc(100vh-100px)]">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 rounded-t-lg">
                    <TabsList className="h-auto w-full justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent px-1 md:px-4 py-2 md:py-4 flex-wrap">
                      {Object.entries(TABS_CONFIG).map(([key, { label, icon: Icon }]) => (
                        <TabsTrigger
                          key={key}
                          value={key}
                         className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap"
                        >
                          <div className="flex items-center gap-1 md:gap-2">
                            <Icon className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden sm:inline">{label}</span>
                            <span className="sm:hidden">{label.split(' ')[0]}</span>
                          </div>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {Object.entries(TABS_CONFIG).map(([key, { content: Content }]) => (
                    <TabsContent key={key} value={key}>
                      <div className="flex flex-col h-full">
                        <div className="sticky top-[72px] bg-white z-10 px-4 py-2 border-b rounded-b-lg">
                          <Content />
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashedBoard;